<template>
  <v-container fluid grid-list-md class="container-gestao-taxas">
    <v-card>
      <v-card-title class="primary white--text titulo-gestao-taxas">
        <div>
          <div class="title font-weight-regular">Gestão de Taxas de Seguro</div>
          <div class="caption caption-gestao-taxas" style="opacity:.85">
            {{ seguradora }} · Ramo {{ ramo }} · Versão {{ versaoAtual && versaoAtual.numeroVersao }}
            <span v-if="vigenciaVersaoFormatada"> · Vigência {{ vigenciaVersaoFormatada }}</span>
          </div>
        </div>
        <v-spacer></v-spacer>
        <span v-if="qtdAlteracoes > 0" class="caption">● alterações não salvas</span>
      </v-card-title>

      <v-card-text v-if="exibindoHistorico">
        <historico-versoes
          :versoes="versoes"
          @voltar="fecharHistorico"
          @revertido="onRevertido"
          @erro="notificar($event, 'error')"
        ></historico-versoes>
      </v-card-text>

      <v-card-text v-show="!exibindoHistorico">
        <v-alert :value="!!versaoPendente" color="info" icon="info" outline class="mb-3">
          Existe uma versão agendada para {{ versaoPendenteVigenciaFormatada }} (versão
          {{ versaoPendente && versaoPendente.numeroVersao }}).
        </v-alert>

        <v-layout row wrap align-center class="mb-3 linha-acoes-gestao-taxas">
          <v-flex xs12 sm2 class="pr-3">
            <v-text-field
              label="Seguradora"
              :value="seguradora"
              readonly
              hide-details
              tabindex="-1"
              class="campo-nao-clicavel"
            ></v-text-field>
          </v-flex>
          <v-flex xs12 sm2 class="pr-3">
            <v-text-field
              label="Ramo"
              :value="ramo"
              readonly
              hide-details
              tabindex="-1"
              class="campo-nao-clicavel"
            ></v-text-field>
          </v-flex>
          <v-flex xs12 sm2 class="pr-3">
            <v-menu
              v-model="menuData"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              max-width="290px"
            >
              <v-text-field
                slot="activator"
                label="Vigência a partir de"
                :value="vigenciaFormatada"
                :disabled="salvando"
                readonly
                hide-details
              ></v-text-field>
              <v-date-picker
                v-model="vigencia"
                locale="pt-br"
                :min="dataMinima"
                no-title
                @input="onVigenciaSelecionada"
              ></v-date-picker>
            </v-menu>
          </v-flex>
          <v-flex xs12 sm6 class="botoes-gestao-taxas">
            <v-btn flat color="primary" :disabled="salvando" @click="abrirHistorico">
              Histórico de Versões
            </v-btn>
            <v-btn
              flat
              color="primary"
              :disabled="coberturasAjusteMassa.length === 0 || salvando"
              @click="abrirAjusteMassa"
            >% Ajuste em Massa</v-btn>
            <v-btn
              flat
              :disabled="qtdAlteracoes === 0 || salvando"
              @click="confirmarCancelamento = true"
            >
              Cancelar
            </v-btn>
            <v-btn
              color="success"
              :disabled="qtdAlteracoes === 0 || salvando"
              :loading="salvando"
              @click="salvar"
            >
              Salvar Alterações
              <v-chip v-if="qtdAlteracoes > 0" small color="white" text-color="success" class="ml-2">
                {{ qtdAlteracoes }}
              </v-chip>
            </v-btn>
          </v-flex>
        </v-layout>

        <v-tabs v-model="aba" color="transparent" slider-color="primary">
          <v-tab v-for="cobertura in coberturas" :key="cobertura.tabela">
            {{ cobertura.nome }}
          </v-tab>
        </v-tabs>

        <div class="caption grey--text py-2">
          <span class="legenda-alterada"></span> Alterado nesta sessão
          <span v-if="ehCoberturaPeriodo || ehCoberturaAtributo" class="ml-3">
            <span class="legenda-nao-aplicavel">{{ textoNaoAplicavel }}</span> Não aplicável
          </span>
          <template v-if="!ehCoberturaPeriodo && !ehCoberturaAtributo">
            <span class="ml-3 legenda-indisponivel"></span> Sem cobertura para a categoria
          </template>
          <span class="ml-3">Clique em uma célula para editar a taxa</span>
        </div>

        <div class="grid-taxas">
          <table class="tabela-taxas">
            <thead>
              <tr>
                <th v-if="cabecalhoLinha">{{ cabecalhoLinha }}</th>
                <template v-else>
                  <th>IS De</th>
                  <th>IS Até</th>
                </template>
                <th v-for="coluna in colunasAtivas" :key="coluna.id">{{ coluna.titulo }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(faixa, faixaIdx) in faixasAtivas" :key="`${tabelaAtiva}|${faixaIdx}`">
                <td v-if="cabecalhoLinha" class="faixa">{{ faixa.titulo }}</td>
                <template v-else>
                  <td class="faixa">{{ formatarIS(faixa.isMin) }}</td>
                  <td class="faixa">{{ formatarIS(faixa.isMax) }}</td>
                </template>
                <td
                  v-for="celula in faixa.taxas"
                  :key="idColuna(celula)"
                  :class="{
                    alterada: estaAlterada(faixaIdx, idColuna(celula)),
                    'nao-aplicavel': naoAplicavel(celula),
                    indisponivel: semTaxa(celula) && !naoAplicavel(celula)
                  }"
                  :title="naoAplicavel(celula) ? 'Combinação não aplicável para esta categoria' : null"
                >
                  <template v-if="naoAplicavel(celula)">{{ rotuloNaoAplicavel(faixa) }}</template>
                  <v-tooltip v-else-if="semTaxa(celula)" bottom>
                    <div slot="activator" class="celula-indisponivel">—</div>
                    <span>Cobertura não disponível para esta categoria</span>
                  </v-tooltip>
                  <input
                    v-else
                    type="text"
                    :value="formatarCelula(celula.fator, faixa)"
                    :disabled="salvando"
                    :inputmode="tipoValorDa(faixa) === 'TAXA' ? null : 'decimal'"
                    @focus="onCelulaFocus($event, faixa)"
                    @click="protegerMascara($event.target, faixa)"
                    @keydown="onCelulaKeydown($event, faixa)"
                    @input="onCelulaInput($event, faixa)"
                    @blur="onCelulaBlur($event, faixaIdx, faixa, celula)"
                    @keyup.enter="$event.target.blur()"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="caption grey--text pt-2">{{ rotuloRodape }}</div>
      </v-card-text>
    </v-card>

    <v-dialog v-model="confirmarCancelamento" max-width="420">
      <v-card>
        <v-card-title class="primary white--text">Descartar alterações?</v-card-title>
        <v-card-text>
          As {{ qtdAlteracoes }} taxa(s) alteradas nesta sessão voltarão ao valor anterior.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn flat @click="confirmarCancelamento = false">Voltar</v-btn>
          <v-btn color="error" @click="cancelarAlteracoes">Descartar Alterações</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" :color="snackbarCor" :timeout="4000">
      {{ snackbarTexto }}
    </v-snackbar>

    <ajuste-em-massa-modal
      v-model="modalAjusteMassa"
      :categorias="categorias"
      :coberturas="coberturasAjusteMassa"
      :tabela-ativa="tabelaAtiva"
      @aplicar="onAjusteMassa"
    ></ajuste-em-massa-modal>

    <v-dialog v-model="modalSaida" max-width="480" persistent>
      <v-card>
        <v-card-title class="primary white--text">Alterações pendentes</v-card-title>
        <v-card-text>
          Existem {{ qtdAlteracoes }} taxa(s) alteradas e não salvas. O que deseja fazer?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn flat :disabled="salvando" @click="continuarEditando">Continuar editando</v-btn>
          <v-btn flat color="error" :disabled="salvando" @click="descartarESair">Descartar</v-btn>
          <v-btn color="success" :disabled="salvando" :loading="salvando" @click="salvarESair">
            Salvar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import axios from 'axios';
import usuarioHelper from '@/helpers/usuario-logado.js';
import AjusteEmMassaModal from './AjusteEmMassaModal';
import HistoricoVersoes from './HistoricoVersoes';
import gestaoTaxasHelper from './gestao-taxas-helper';

function dataLocalHoje() {
  const agora = new Date();
  const mes = String(agora.getMonth() + 1).padStart(2, '0');
  const dia = String(agora.getDate()).padStart(2, '0');
  return `${agora.getFullYear()}-${mes}-${dia}`;
}

export default {
  name: 'GestaoTaxasSeguro',
  components: { AjusteEmMassaModal, HistoricoVersoes },
  data() {
    return {
      aba: 0,
      seguradora: 'ESSOR Seguros',
      ramo: 'Frotas / Auto',
      versaoAtual: null,
      versaoPendente: null,
      coberturas: [],
      categorias: [],
      alteracoes: {},
      vigencia: dataLocalHoje(),
      dataMinima: dataLocalHoje(),
      menuData: false,
      confirmarCancelamento: false,
      salvando: false,
      snackbar: false,
      snackbarTexto: '',
      snackbarCor: 'success',
      modalAjusteMassa: false,
      exibindoHistorico: false,
      versoes: [],
      modalSaida: false,
      textoNaoAplicavel: gestaoTaxasHelper.TEXTO_NAO_APLICAVEL,
      acaoPendente: null,
      vigenciaAnterior: null
    };
  },
  computed: {
    coberturaAtiva() {
      return this.coberturas[this.aba] || this.coberturas[0] || null;
    },
    tabelaAtiva() {
      return this.coberturaAtiva ? this.coberturaAtiva.tabela : '';
    },
    faixasAtivas() {
      return this.coberturaAtiva ? this.coberturaAtiva.faixas || [] : [];
    },
    ehCoberturaKm() {
      return gestaoTaxasHelper.ehCoberturaKm(this.coberturaAtiva);
    },
    ehCoberturaAtributo() {
      return gestaoTaxasHelper.ehCoberturaAtributo(this.coberturaAtiva);
    },
    ehCoberturaPeriodo() {
      return gestaoTaxasHelper.ehCoberturaPeriodo(this.coberturaAtiva);
    },
    ehCoberturaMoeda() {
      return gestaoTaxasHelper.ehCoberturaMoeda(this.coberturaAtiva);
    },
    cabecalhoLinha() {
      return gestaoTaxasHelper.cabecalhoLinha(this.coberturaAtiva);
    },
    coberturasAjusteMassa() {
      return gestaoTaxasHelper.coberturasAjusteMassa(this.coberturas);
    },
    colunasAtivas() {
      return gestaoTaxasHelper.colunasDa(this.coberturaAtiva, this.categorias);
    },
    rotuloRodape() {
      return gestaoTaxasHelper.rotuloRodape(this.coberturaAtiva, this.faixasAtivas.length);
    },
    qtdAlteracoes() {
      return Object.keys(this.alteracoes).length;
    },
    vigenciaFormatada() {
      if (!this.vigencia) return '';
      const [ano, mes, dia] = this.vigencia.split('-');
      return `${dia}/${mes}/${ano}`;
    },

    vigenciaVersaoFormatada() {
      if (!this.versaoAtual || !this.versaoAtual.inicioVigencia) return '';
      const inicio = this.formatarData(this.versaoAtual.inicioVigencia);
      if (!this.versaoAtual.fimVigencia) return inicio;
      return `${inicio} a ${this.formatarData(this.versaoAtual.fimVigencia)}`;
    },
    versaoPendenteVigenciaFormatada() {
      if (!this.versaoPendente || !this.versaoPendente.inicioVigencia) return '';
      return this.formatarData(this.versaoPendente.inicioVigencia);
    }
  },
  created() {
    this.vigenciaAnterior = this.vigencia;
    this.carregarGrid();
    window.addEventListener('beforeunload', this.avisarSaida);
  },
  beforeDestroy() {
    window.removeEventListener('beforeunload', this.avisarSaida);
  },
  beforeRouteLeave(to, from, next) {
    if (this.qtdAlteracoes === 0) {
      next();
      return;
    }
    this.acaoPendente = next;
    this.modalSaida = true;
  },
  methods: {
    cabecalho() {
      return { headers: { Authorization: `Bearer ${usuarioHelper.USUARIO_LOGADO().token}` } };
    },
    chave(tabela, faixaIdx, idColuna) {
      return gestaoTaxasHelper.chaveAlteracao(tabela, faixaIdx, idColuna);
    },
    semTaxa(celula) {
      return !celula || celula.fator === null || celula.fator === undefined || celula.fator === '';
    },
    estaAlterada(faixaIdx, idColuna) {
      return this.alteracoes[this.chave(this.tabelaAtiva, faixaIdx, idColuna)] !== undefined;
    },
    naoAplicavel(celula) {
      return gestaoTaxasHelper.celulaNaoAplicavel(this.coberturaAtiva, celula);
    },
    idColuna(celula) {
      return gestaoTaxasHelper.idColunaDaCelula(this.coberturaAtiva, celula);
    },
    coberturaDe(tabela) {
      return this.coberturas.find(c => c.tabela === tabela) || null;
    },
    faixasDa(tabela) {
      const cobertura = this.coberturaDe(tabela);
      return cobertura ? cobertura.faixas || [] : [];
    },
    celulaDe(tabela, faixaIdx, idColuna) {
      const faixa = this.faixasDa(tabela)[faixaIdx];
      if (!faixa) return null;
      const cobertura = this.coberturaDe(tabela);
      return (
        (faixa.taxas || []).find(
          t => gestaoTaxasHelper.idColunaDaCelula(cobertura, t) === idColuna
        ) || null
      );
    },
    formatarIS(valor) {
      return gestaoTaxasHelper.formatarIS(valor);
    },
    formatarCelula(valor, faixa) {
      return gestaoTaxasHelper.formatarValorCelula(this.coberturaAtiva, valor, faixa);
    },
    tipoValorDa(faixa) {
      return gestaoTaxasHelper.tipoValor(this.coberturaAtiva, faixa);
    },
    rotuloNaoAplicavel(faixa) {
      return gestaoTaxasHelper.rotuloNaoAplicavel(this.coberturaAtiva, faixa);
    },
    formatarData(data) {
      if (!data) return '';
      const [ano, mes, dia] = String(data).substr(0, 10).split('-');
      return `${dia}/${mes}/${ano}`;
    },
    aplicarPayload(dados) {
      this.categorias = dados.categorias || [];
      this.coberturas = dados.coberturas || [];
      if (this.aba >= this.coberturas.length) this.aba = 0;
    },
    carregarGrid() {
      axios
        .get(`${process.env.URL_GESTAOTAXASMS}/v1/grid?data=${this.vigencia}`, this.cabecalho())
        .then(response => {
          this.seguradora = response.data.seguradora;
          this.ramo = response.data.ramo;
          this.versaoAtual = response.data.versao;
          this.versaoPendente = response.data.versaoPendente || null;
          this.aplicarPayload(response.data);
          this.alteracoes = {};
        })
        .catch(() => this.notificar('Não foi possível carregar as taxas.', 'error'));
    },
    onCelulaFocus(evento, faixa) {
      const campo = evento.target;
      const tipo = this.tipoValorDa(faixa);
      if (tipo === gestaoTaxasHelper.VALOR_PERCENTUAL) {
        campo.value = gestaoTaxasHelper.mascararPercentual(campo.value);
        campo.dataset.anterior = campo.value;
        campo.setSelectionRange(0, this.fimDigitosPercentual(campo));
        return;
      }
      if (tipo !== gestaoTaxasHelper.VALOR_MOEDA) {
        campo.select();
        return;
      }
      const prefixo = gestaoTaxasHelper.PREFIXO_MOEDA;
      campo.value = gestaoTaxasHelper.mascararMoeda(campo.value);
      campo.dataset.anterior = campo.value;
      campo.setSelectionRange(prefixo.length, campo.value.length);
    },
    fimDigitosPercentual(campo) {
      const sufixo = gestaoTaxasHelper.SUFIXO_PERCENTUAL;
      return campo.value.endsWith(sufixo) ? campo.value.length - sufixo.length : campo.value.length;
    },
    protegerMascara(campo, faixa) {
      const tipo = this.tipoValorDa(faixa);
      if (tipo === gestaoTaxasHelper.VALOR_PERCENTUAL) {
        const fim = this.fimDigitosPercentual(campo);
        if (campo.selectionEnd > fim) {
          campo.setSelectionRange(Math.min(campo.selectionStart, fim), fim);
        }
        return;
      }
      if (tipo === gestaoTaxasHelper.VALOR_MOEDA) this.protegerPrefixoMoeda(campo);
    },
    protegerPrefixoMoeda(campo) {
      const tamanho = gestaoTaxasHelper.PREFIXO_MOEDA.length;
      if (campo.selectionStart < tamanho) {
        campo.setSelectionRange(tamanho, Math.max(tamanho, campo.selectionEnd));
      }
    },
    onCelulaKeydown(evento, faixa) {
      if (evento.ctrlKey || evento.metaKey || evento.altKey) return;
      const tipo = this.tipoValorDa(faixa);
      if (tipo === gestaoTaxasHelper.VALOR_PERCENTUAL) {
        this.onPercentualKeydown(evento);
        return;
      }
      if (tipo !== gestaoTaxasHelper.VALOR_MOEDA) {
        if (evento.key.length === 1 && !/[\d,]/.test(evento.key)) evento.preventDefault();
        return;
      }
      const campo = evento.target;
      const tamanho = gestaoTaxasHelper.PREFIXO_MOEDA.length;
      const inicio = campo.selectionStart;
      const fim = campo.selectionEnd;

      if (['Backspace', 'ArrowLeft', 'ArrowUp'].indexOf(evento.key) !== -1) {
        if (inicio === fim && inicio <= tamanho) evento.preventDefault();
        else if (inicio < tamanho) campo.setSelectionRange(tamanho, fim);
        return;
      }
      if (evento.key === 'Delete') {
        this.protegerPrefixoMoeda(campo);
        return;
      }
      if (evento.key === 'Home') {
        evento.preventDefault();
        campo.setSelectionRange(tamanho, evento.shiftKey ? fim : tamanho);
        return;
      }
      if (evento.key === '.') {
        evento.preventDefault();
        campo.setRangeText(',', Math.max(inicio, tamanho), Math.max(fim, tamanho), 'end');
        this.onCelulaInput(evento, faixa);
        return;
      }
      if (evento.key.length === 1 && !/[\d,]/.test(evento.key)) {
        evento.preventDefault();
      }
    },
    onPercentualKeydown(evento) {
      const campo = evento.target;
      const fim = this.fimDigitosPercentual(campo);
      if (evento.key === 'End' || (evento.key === 'ArrowRight' && campo.selectionEnd >= fim)) {
        evento.preventDefault();
        campo.setSelectionRange(evento.shiftKey ? campo.selectionStart : fim, fim);
        return;
      }
      if (evento.key === 'Delete' && campo.selectionStart === campo.selectionEnd
        && campo.selectionStart >= fim) {
        evento.preventDefault();
        return;
      }
      if (evento.key.length === 1 && !/\d/.test(evento.key)) evento.preventDefault();
    },
    onPercentualInput(campo) {
      const bruto = campo.value;
      const digitosAntes = bruto.slice(0, campo.selectionStart).replace(/\D/g, '').length;
      const mascarado = gestaoTaxasHelper.mascararPercentual(bruto);
      campo.value = mascarado;
      campo.dataset.anterior = mascarado;
      const fim = this.fimDigitosPercentual(campo);
      const posicao = Math.min(digitosAntes, fim);
      campo.setSelectionRange(posicao, posicao);
    },
    onCelulaInput(evento, faixa) {
      const tipo = this.tipoValorDa(faixa);
      if (tipo === gestaoTaxasHelper.VALOR_PERCENTUAL) {
        this.onPercentualInput(evento.target);
        return;
      }
      if (tipo !== gestaoTaxasHelper.VALOR_MOEDA) {
        this.mascararTaxaDigitada(evento.target);
        return;
      }
      const campo = evento.target;
      const bruto = campo.value;
      const anterior = campo.dataset.anterior || gestaoTaxasHelper.PREFIXO_MOEDA;

      if (gestaoTaxasHelper.excedeLimiteMoeda(bruto)) {
        const recuo = Math.max(0, bruto.length - anterior.length);
        const posicao = Math.max(
          gestaoTaxasHelper.PREFIXO_MOEDA.length, campo.selectionStart - recuo);
        campo.value = anterior;
        campo.setSelectionRange(posicao, posicao);
        return;
      }

      const significativos = gestaoTaxasHelper.contarSignificativos(bruto, campo.selectionStart);
      const mascarado = gestaoTaxasHelper.mascararMoeda(bruto);
      const posicao = gestaoTaxasHelper.posicaoAposSignificativos(mascarado, significativos);
      campo.value = mascarado;
      campo.dataset.anterior = mascarado;
      campo.setSelectionRange(posicao, posicao);
    },
    mascararTaxaDigitada(campo) {
      const digitado = String(campo.value);
      const mascarado = gestaoTaxasHelper.mascararTaxa(digitado);
      if (mascarado === digitado) return;
      const posicao = gestaoTaxasHelper
        .mascararTaxa(digitado.slice(0, campo.selectionStart)).length;
      campo.value = mascarado;
      campo.setSelectionRange(posicao, posicao);
    },
    onCelulaBlur(evento, faixaIdx, faixa, celula) {
      const cobertura = this.coberturaAtiva;
      const valor = gestaoTaxasHelper.parseValorCelula(cobertura, evento.target.value, faixa);
      if (valor === null) {
        evento.target.value = this.formatarCelula(celula.fator, faixa);
        return;
      }
      const arredondado = gestaoTaxasHelper.arredondar(cobertura, valor, faixa);
      if (!gestaoTaxasHelper.valorValido(cobertura, arredondado, faixa)) {
        evento.target.value = this.formatarCelula(celula.fator, faixa);
        this.notificar(
          `Valor inválido: deve ser ${gestaoTaxasHelper.limiteDescrito(cobertura, faixa)}.`,
          'error');
        return;
      }
      const idColuna = gestaoTaxasHelper.idColunaDaCelula(cobertura, celula);
      this.aplicarAlteracao(this.tabelaAtiva, faixaIdx, idColuna, valor, 'Edição manual');
      evento.target.value = this.formatarCelula(celula.fator, faixa);
    },
    aplicarAlteracao(tabela, faixaIdx, idColuna, valor, origem) {
      const cobertura = this.coberturaDe(tabela);
      const faixa = this.faixasDa(tabela)[faixaIdx];
      const celula = this.celulaDe(tabela, faixaIdx, idColuna);
      if (!faixa || !celula) return;

      const chave = this.chave(tabela, faixaIdx, idColuna);
      const anterior =
        this.alteracoes[chave] !== undefined ? this.alteracoes[chave].anterior : celula.fator;
      const arredondado = gestaoTaxasHelper.arredondar(cobertura, valor, faixa);

      celula.fator = arredondado;

      if (Number(anterior) === arredondado) {
        this.$delete(this.alteracoes, chave);
        return;
      }
      const alteracao = gestaoTaxasHelper.montarAlteracao(
        cobertura, faixa, celula, arredondado, origem);
      alteracao.anterior = anterior;
      this.$set(this.alteracoes, chave, alteracao);
    },
    descartarAlteracoes() {
      Object.keys(this.alteracoes).forEach(chave => {
        const alteracao = this.alteracoes[chave];
        const faixaIdx = Number(chave.split('|')[1]);
        const idColuna = gestaoTaxasHelper.idColunaDaAlteracao(alteracao);
        const celula = this.celulaDe(alteracao.tabela, faixaIdx, idColuna);
        if (celula) celula.fator = alteracao.anterior;
      });
      this.alteracoes = {};
    },
    cancelarAlteracoes() {
      this.descartarAlteracoes();
      this.confirmarCancelamento = false;
      this.carregarGrid();
      this.notificar('Alterações descartadas.', 'info');
    },
    salvar() {
      if (this.salvando) return Promise.resolve();
      this.salvando = true;
      const corpo = {
        inicioVigencia: this.vigencia,
        alteracoes: Object.keys(this.alteracoes).map(chave => {
          const alteracao = this.alteracoes[chave];
          return {
            tabela: alteracao.tabela,
            isMin: alteracao.isMin,
            isMax: alteracao.isMax,
            idCatVeiculo: alteracao.idCatVeiculo,
            idKmAssistencia: alteracao.idKmAssistencia,
            idOrigemVeiculo: alteracao.idOrigemVeiculo,
            atributo: alteracao.atributo,
            diasCarroReserva: alteracao.diasCarroReserva,
            fator: alteracao.atual,
            origem: alteracao.origem
          };
        })
      };
      return axios
        .post(`${process.env.URL_GESTAOTAXASMS}/v1`, corpo, this.cabecalho())
        .then(response => {
          this.salvando = false;
          this.versaoAtual = response.data.versao;
          this.versaoPendente = response.data.versaoPendente || null;
          this.aplicarPayload(response.data);
          this.alteracoes = {};
          this.notificar('Alterações salvas com sucesso.', 'success');
        })
        .catch(erro => {
          this.salvando = false;
          const mensagem =
            erro.response && erro.response.data && erro.response.data.message
              ? erro.response.data.message
              : 'Não foi possível salvar as alterações.';
          this.notificar(mensagem, 'error');
          return Promise.reject(erro);
        });
    },
    onVigenciaSelecionada() {
      this.menuData = false;
      if (this.qtdAlteracoes > 0) {
        this.acaoPendente = null;
        this.modalSaida = true;
        return;
      }
      this.vigenciaAnterior = this.vigencia;
      this.carregarGrid();
    },
    avisarSaida(evento) {
      if (this.qtdAlteracoes === 0) return undefined;
      evento.preventDefault();
      evento.returnValue = '';
      return '';
    },
    abrirAjusteMassa() {
      this.modalAjusteMassa = true;
    },
    onAjusteMassa(payload) {
      payload.alteracoes.forEach(item => {
        this.aplicarAlteracao(
          payload.tabela, item.faixaIdx, item.idColuna, item.valor, payload.origem);
      });
    },
    abrirHistorico() {
      this.carregarVersoes()
        .then(() => {
          this.exibindoHistorico = true;
          window.scrollTo(0, 0);
        })
        .catch(() => this.notificar('Não foi possível carregar o histórico.', 'error'));
    },
    fecharHistorico() {
      this.exibindoHistorico = false;
      window.scrollTo(0, 0);
    },
    carregarVersoes() {
      return axios
        .get(`${process.env.URL_GESTAOTAXASMS}/v1/versoes`, this.cabecalho())
        .then(response => {
          this.versoes = response.data;
        });
    },

    onRevertido() {
      this.alteracoes = {};
      this.vigencia = dataLocalHoje();
      this.vigenciaAnterior = this.vigencia;
      this.carregarGrid();
      this.notificar('Versão revertida com sucesso.', 'success');
      this.carregarVersoes().catch(() => {
        this.notificar('Reversão feita, mas não foi possível recarregar o histórico.', 'info');
      });
    },
    continuarEditando() {
      this.modalSaida = false;
      if (this.acaoPendente) {
        this.acaoPendente(false);
        this.acaoPendente = null;
      }
      this.vigencia = this.vigenciaAnterior;
    },
    descartarESair() {
      this.descartarAlteracoes();
      this.modalSaida = false;
      this.concluirAcaoPendente();
    },
    salvarESair() {
      this.salvar()
        .then(() => {
          this.modalSaida = false;
          this.concluirAcaoPendente();
        })
        .catch(() => {
          this.modalSaida = false;
          if (this.acaoPendente) {
            this.acaoPendente(false);
            this.acaoPendente = null;
          }
        });
    },
    concluirAcaoPendente() {
      if (this.acaoPendente) {
        this.acaoPendente();
        this.acaoPendente = null;
      } else {
        this.vigenciaAnterior = this.vigencia;
        this.carregarGrid();
      }
    },
    notificar(texto, cor) {
      this.snackbarTexto = texto;
      this.snackbarCor = cor;
      this.snackbar = true;
    }
  }
};
</script>

<style scoped>
.botoes-gestao-taxas {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
}
.botoes-gestao-taxas .v-btn {
  margin-top: 0;
  margin-bottom: 0;
}
.linha-acoes-gestao-taxas .v-text-field {
  margin-top: 0;
}
.container-gestao-taxas {
  padding: 12px;
}
.titulo-gestao-taxas {
  padding-top: 8px;
  padding-bottom: 8px;
}
.caption-gestao-taxas {
  font-size: 11px !important;
}
.grid-taxas {
  overflow-x: auto;
  border: 1px solid #eee;
  border-radius: 4px;
}
.tabela-taxas {
  border-collapse: collapse;
  width: 100%;
  min-width: 1100px;
}
.tabela-taxas th {
  background: #184170;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  padding: 9px 8px;
  text-align: center;
  white-space: nowrap;
}
.tabela-taxas td {
  padding: 0;
  border: 1px solid #e3e3e3;
  text-align: center;
  font-size: 13px;
  white-space: nowrap;
  background: #f2f2f2;
}
.tabela-taxas tr:nth-child(even) td {
  background: #e6e6e6;
}
.tabela-taxas td.faixa {
  font-weight: 600;
  color: #184170;
  background: #fff !important;
  padding: 9px 12px;
}
.tabela-taxas td.indisponivel {
  background: repeating-linear-gradient(
    45deg,
    #ededed,
    #ededed 5px,
    #e0e0e0 5px,
    #e0e0e0 10px
  ) !important;
  cursor: not-allowed;
}
.celula-indisponivel {
  padding: 9px 6px;
  color: #9e9e9e;
  cursor: not-allowed;
  user-select: none;
}
.tabela-taxas td.alterada {
  background: #fff3cd !important;
  box-shadow: inset 3px 0 0 #f5a623;
}
.tabela-taxas td.nao-aplicavel {
  color: #9e9e9e;
  font-style: italic;
  cursor: not-allowed;
  padding: 9px 6px;
  background: #fafafa !important;
}
.tabela-taxas td input {
  width: 100%;
  border: none;
  background: transparent;
  text-align: center;
  font-size: 13px;
  padding: 9px 6px;
}
.tabela-taxas td input:focus {
  outline: 2px solid #184170;
  outline-offset: -2px;
  background: #fff;
}
.campo-nao-clicavel {
  pointer-events: none;
}
.legenda-nao-aplicavel {
  display: inline-block;
  padding: 0 4px;
  border: 1px solid #e0e0e0;
  border-radius: 2px;
  background: #fafafa;
  color: #9e9e9e;
  font-style: italic;
  margin-right: 2px;
}
.legenda-alterada {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 2px;
  background: #f5a623;
  margin-right: 4px;
  vertical-align: -1px;
}
.legenda-indisponivel {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 2px;
  background: repeating-linear-gradient(45deg, #ededed, #ededed 3px, #c9c9c9 3px, #c9c9c9 6px);
  margin-right: 4px;
  vertical-align: -1px;
}
</style>



// WEBPACK FOOTER //
// src/components/pages/gestao-taxas/GestaoTaxasSeguro.vue